/* ==========================================================================
   NaturoBest Landing Page System v2 — nb2-landing.js
   --------------------------------------------------------------------------
   Independent of nb-landing.js: different selectors, different data
   attributes, no shared globals. Both files can load on the same page without
   fighting over the same nodes.

   Dependency-free. Add to cart itself is handled by the theme's own
   <product-form> custom element (assets/theme-global.js), so nothing here
   touches the cart. This file only does presentation:

     1. Variant toggle (keeps switchers in step, updates price and size)
     2. FAQ accordion (one open at a time, animated)
     3. Review carousel (controls for a native scroll-snap track)
     4. Sticky buy bar reveal
     5. Anchor nav — active state, and scrolling without a URL hash
     6. Promo bar rotator (cross-fades the top strip's messages)

   4 and 5 are ports of the v1 behaviours the NB2 template used to borrow from
   nb-landing.js. They were copied rather than shared so an NB2 page loads only
   nb2-* assets; v1 keeps its own copies untouched for page.nb-landing. v1s
   video facade is not ported — v2 has no video section.

   Re-runs on Shopify theme editor section load.
   ========================================================================== */

(function () {
  'use strict';

  /* 1. Variant toggle ----------------------------------------------------- */

  /* The radios themselves are named "id", so the checked one is what the
     theme's <product-form> submits. Nothing here touches their values —
     rewriting the first radio's value (as this used to) made switching back to
     the first size add the other one.

     A page can carry several switchers for one product (hero, sticky bar,
     final CTA). Picking a size in any of them selects it in all of them, and
     updates every price for that product: the one inside each buy form and the
     hero / final CTA price line with its size name. */

  function selectVariant(productId, variantId) {
    var groups = document.querySelectorAll('[data-nb2-variants]');
    var chosen = null;

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.nb2ProductId !== productId) return;

      var input = group.querySelector('input[type="radio"][value="' + variantId + '"]');
      if (!input) return;
      input.checked = true;
      chosen = chosen || input;

      var buy = group.closest('[data-nb2-buy]') || group.closest('form');
      var priceNode = buy && buy.querySelector('.nb2-buy__price');
      if (priceNode && input.dataset.nb2VariantPrice) {
        priceNode.innerHTML = input.dataset.nb2VariantPrice;
      }

      var submitButton = buy && buy.querySelector('[name="add"]');
      if (submitButton) {
        var soldOut = input.dataset.nb2Available === 'false';
        submitButton.disabled = soldOut;
        submitButton.textContent = soldOut
          ? submitButton.dataset.nb2SoldOutLabel || 'Sold out'
          : submitButton.dataset.nb2Label || submitButton.textContent;
      }
    });

    if (!chosen) return;

    var lines = document.querySelectorAll('[data-nb2-price-line]');
    Array.prototype.forEach.call(lines, function (line) {
      if (line.dataset.nb2ProductId !== productId) return;

      var amount = line.querySelector('[data-nb2-price-amount]');
      if (amount && chosen.dataset.nb2VariantPriceFull) {
        amount.innerHTML = chosen.dataset.nb2VariantPriceFull;
      }

      // No prefix added here — any dot or wording around the size sits in the
      // merchant's own Price note text (outside this span), from the {{ size }}
      // placeholder in snippets/nb2-price-line.liquid.
      var size = line.querySelector('[data-nb2-price-size]');
      if (size && chosen.dataset.nb2VariantTitle) {
        size.textContent = chosen.dataset.nb2VariantTitle;
      }
    });
  }

  function initVariantToggle(root) {
    var groups = root.querySelectorAll('[data-nb2-variants]');

    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.nb2Ready === 'true') return;
      group.dataset.nb2Ready = 'true';

      group.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.type !== 'radio' || !input.checked) return;
        selectVariant(group.dataset.nb2ProductId, input.value);
      });
    });
  }

  /* 2. FAQ accordion ------------------------------------------------------ */

  /* <details> has no height to animate between — it jumps from summary-only to
     full. So the click is intercepted and the height is driven here, which also
     makes closing a sibling a real transition rather than a snap.

     The markup still carries the native exclusive `name` attribute. That is the
     no-JavaScript path; once this takes over it drops the attribute, otherwise
     the browser would slam the siblings shut mid-animation. */

  var FAQ_DURATION = 260;
  var FAQ_EASING = 'cubic-bezier(0.33, 1, 0.68, 1)';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /* Height with the answer hidden: the summary plus the item's own borders,
     which offsetHeight counts and clientHeight does not. */
  function closedHeight(item, summary) {
    return summary.offsetHeight + item.offsetHeight - item.clientHeight;
  }

  function stop(item) {
    if (!item.nb2Animation) return;
    item.nb2Animation.cancel();
    item.nb2Animation = null;
  }

  function slide(item, from, to, onDone) {
    item.style.overflow = 'hidden';

    var animation = item.animate(
      { height: [from + 'px', to + 'px'] },
      { duration: FAQ_DURATION, easing: FAQ_EASING }
    );
    item.nb2Animation = animation;

    animation.onfinish = function () {
      item.nb2Animation = null;
      item.style.overflow = '';
      if (onDone) onDone();
    };
  }

  function closeItem(item, summary) {
    // Read before stopping: cancelling drops the animated height.
    var from = item.offsetHeight;
    stop(item);

    if (!summary || reducedMotion()) {
      item.removeAttribute('data-nb2-closing');
      item.open = false;
      return;
    }

    // Drives the + back round now rather than when the slide lands.
    item.setAttribute('data-nb2-closing', '');

    slide(item, from, closedHeight(item, summary), function () {
      item.removeAttribute('data-nb2-closing');
      item.open = false;
    });
  }

  function openItem(item, list) {
    var others = list.querySelectorAll('details[open]');
    Array.prototype.forEach.call(others, function (other) {
      if (other !== item && !other.hasAttribute('data-nb2-closing')) {
        closeItem(other, other.querySelector('summary'));
      }
    });

    var from = item.offsetHeight;
    stop(item);
    item.removeAttribute('data-nb2-closing');
    item.open = true;

    if (reducedMotion()) return;
    slide(item, from, item.offsetHeight);
  }

  function initFaq(root) {
    var lists = root.querySelectorAll('[data-nb2-faq]');
    if (!lists.length || typeof Element.prototype.animate !== 'function') return;

    Array.prototype.forEach.call(lists, function (list) {
      if (list.dataset.nb2Ready === 'true') return;
      list.dataset.nb2Ready = 'true';

      var items = list.querySelectorAll('details');

      Array.prototype.forEach.call(items, function (item) {
        var summary = item.querySelector('summary');
        if (!summary) return;

        item.removeAttribute('name');

        summary.addEventListener('click', function (event) {
          event.preventDefault();

          if (item.open && !item.hasAttribute('data-nb2-closing')) {
            closeItem(item, summary);
          } else {
            openItem(item, list);
          }
        });
      });
    });
  }

  /* 3. Review carousel ---------------------------------------------------- */

  /* The track is a plain scroll-snap flexbox, so the swipe, the trackpad and
     the arrow keys all come free from the browser. This adds the arrows and the
     dots, and only when they earn their place: three reviews fill a desktop row
     exactly, nothing overflows, and the controls stay hidden — the section is
     then indistinguishable from the static grid it replaced. The client adding
     a fourth review in the theme editor is what turns it into a carousel.

     Pages, not reviews: twelve reviews three-up is four dots, not twelve. */

  function carouselPages(track) {
    var per = track.clientWidth;
    if (per <= 0) return 1;
    // -1 absorbs the sub-pixel remainder a fractional card width leaves behind,
    // which would otherwise round a full row up to a second, empty page.
    return Math.max(1, Math.ceil((track.scrollWidth - 1) / per));
  }

  function carouselPage(track, pages) {
    var per = track.clientWidth;
    var max = track.scrollWidth - per;
    if (per <= 0 || max <= 0) return 0;

    /* The last page is short — its offset sits less than a full page past the
       one before it, so plain rounding would leave the dot a step behind at the
       end of the track. */
    if (track.scrollLeft >= max - 1) return pages - 1;

    return Math.min(pages - 1, Math.max(0, Math.round(track.scrollLeft / per)));
  }

  function initReviewCarousel(root) {
    var carousels = root.querySelectorAll('[data-nb2-carousel]');

    Array.prototype.forEach.call(carousels, function (carousel) {
      if (carousel.dataset.nb2Ready === 'true') return;
      carousel.dataset.nb2Ready = 'true';

      var track = carousel.querySelector('[data-nb2-carousel-track]');
      var controls = carousel.querySelector('[data-nb2-carousel-controls]');
      if (!track || !controls) return;

      var prev = controls.querySelector('[data-nb2-carousel-prev]');
      var next = controls.querySelector('[data-nb2-carousel-next]');
      var dots = controls.querySelector('[data-nb2-carousel-dots]');
      var dotLabel = (dots && dots.getAttribute('data-nb2-dot-label')) || 'Page';
      var buttons = [];
      var frame = null;

      function scrollToPage(index) {
        // Past either end the browser clamps for us, so no bounds check here.
        track.scrollTo({
          left: index * track.clientWidth,
          behavior: reducedMotion() ? 'auto' : 'smooth'
        });
      }

      function renderDots(count) {
        if (!dots || count === buttons.length) return;

        dots.innerHTML = '';
        buttons = [];

        for (var i = 0; i < count; i++) {
          buttons.push(addDot(i));
        }
      }

      function addDot(index) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'nb2-reviews__dot';
        dot.setAttribute('aria-label', dotLabel + ' ' + (index + 1));
        dot.addEventListener('click', function () {
          scrollToPage(index);
        });
        dots.appendChild(dot);
        return dot;
      }

      function sync() {
        var scrollable = track.scrollWidth - track.clientWidth > 1;

        controls.hidden = !scrollable;

        /* A scroll container is only worth a tab stop while it has somewhere to
           scroll to. The markup ships with tabindex so the track stays keyboard
           reachable if this file never runs. */
        if (scrollable) {
          track.setAttribute('tabindex', '0');
        } else {
          track.removeAttribute('tabindex');
        }

        if (!scrollable) return;

        var pages = carouselPages(track);
        renderDots(pages);

        var current = carouselPage(track, pages);
        buttons.forEach(function (dot, index) {
          dot.setAttribute('aria-current', index === current ? 'true' : 'false');
        });

        if (prev) prev.disabled = track.scrollLeft <= 1;
        if (next) next.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
      }

      // Scroll fires far faster than paint, and every handler above reads
      // layout, so coalesce them onto the frame.
      function queueSync() {
        if (frame) return;
        frame = requestAnimationFrame(function () {
          frame = null;
          sync();
        });
      }

      function step(direction) {
        scrollToPage(carouselPage(track, carouselPages(track)) + direction);
      }

      if (prev) {
        prev.addEventListener('click', function () { step(-1); });
      }
      if (next) {
        next.addEventListener('click', function () { step(1); });
      }

      track.addEventListener('scroll', queueSync);

      /* Whether the row overflows depends on its width, and the width changes
         without the window doing so — the theme editor opening a panel, or the
         section being re-rendered around it. */
      if (typeof ResizeObserver === 'function') {
        new ResizeObserver(queueSync).observe(track);
      } else {
        window.addEventListener('resize', queueSync);
      }

      sync();
    });
  }

  /* 4. Sticky buy bar ----------------------------------------------------- */

  /* Ported from nb-landing.js so an NB2 page no longer loads the v1 file. Every
     selector, dataset key and custom property is renamed to nb2, so the two can
     still sit on the same store without writing over each other. */

  function initStickyBar(root) {
    var bar = root.querySelector('[data-nb2-sticky-bar]');
    if (!bar || bar.dataset.nb2Ready === 'true') return;
    bar.dataset.nb2Ready = 'true';

    if (bar.dataset.nb2HideThemeMobileBar === 'true') {
      document.body.classList.add('nb2-hide-theme-mobile-bar');
    }

    var docEl = document.documentElement;
    var spacer = document.querySelector('[data-nb2-sticky-spacer]');
    var alwaysOn = bar.dataset.nb2Reveal === 'always';
    var trigger = document.querySelector(bar.dataset.nb2Trigger || '[data-nb2-sticky-trigger]');
    var fallbackOffset = parseInt(bar.dataset.nb2Offset, 10) || 600;

    // The theme header's own height variable (--m-header-height) covers the
    // header element only — it excludes the announcement bar and the topbar.
    // Measuring the wrapper's live bottom edge instead handles the announcement
    // bar, the sticky/non-sticky states and the header's hide-on-scroll-down
    // behaviour in one go.
    var themeHeader =
      document.querySelector('.m-header__wrapper') || document.querySelector('.m-header');
    // The wrapper is what moves; the state class lives on its parent.
    var themeHeaderRoot = document.querySelector('.m-header');

    function headerBottom() {
      if (!themeHeader) return 0;
      var rect = themeHeader.getBoundingClientRect();
      return rect.bottom > 0 ? Math.round(rect.bottom) : 0;
    }

    var ticking = false;
    var followUntil = 0;
    var lastOffset = null;
    var lastStickyH = null;
    var lastSpacer = null;
    var lastStuck = null;
    var settleTimer = null;

    /* The theme flips .m-header to .stuck as it becomes sticky, and that rule
       changes the wrapper from position:relative to position:fixed. A position
       change cannot animate, so the header's bottom edge — and with it this
       bar's top — moves in a single step.

       Easing is switched on only for the length of that switch. Leaving a
       transition on top permanently would be worse: top is resampled every
       frame while scrolling, so the bar would trail the header the whole way
       down instead of sitting under it. */
    function settle() {
      bar.classList.add('is-settling');

      /* Force a style flush before the caller writes the new top.

         A transition needs the property to be present in the element's style
         BEFORE the value changes. Adding the class and moving top inside the
         same frame lands both in one recalc, so the browser sees no "before"
         state to animate from and jumps anyway. Reading a layout property
         commits the class first. */
      void bar.offsetWidth;

      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        bar.classList.remove('is-settling');
      }, 420);
    }

    function update() {
      var stuck = themeHeaderRoot ? themeHeaderRoot.classList.contains('stuck') : false;
      if (lastStuck !== null && stuck !== lastStuck) settle();
      lastStuck = stuck;

      // Position: below whatever the theme header currently occupies.
      var offset = headerBottom() + 'px';
      if (offset !== lastOffset) {
        docEl.style.setProperty('--nb2-header-offset', offset);
        lastOffset = offset;
      }

      // Visibility.
      var visible;
      if (alwaysOn) {
        visible = true;
      } else if (trigger) {
        visible = trigger.getBoundingClientRect().bottom <= headerBottom();
      } else {
        visible = window.scrollY > fallbackOffset;
      }
      bar.classList[visible ? 'add' : 'remove']('is-visible');

      // Reserve layout space only while the bar is permanently on screen.
      var height = bar.offsetHeight;
      var spacerH = alwaysOn ? height + 'px' : '0px';
      if (spacer && spacerH !== lastSpacer) {
        spacer.style.height = spacerH;
        lastSpacer = spacerH;
      }

      /* Anchor targets must clear the bar at every width.

         This variable is measured FROM the bar, so nothing that sizes the bar
         may read it back — see the min-height comment on .nb2-sticky__inner.
         Its only consumer is scroll-margin-top, which affects no layout. */
      var stickyH = height + 'px';
      if (stickyH !== lastStickyH) {
        docEl.style.setProperty('--nb2-sticky-h', stickyH);
        lastStickyH = stickyH;
      }
    }

    /* Keep sampling for a beat after the last scroll event.

       The theme header slides itself in and out over half a second
       (.m-header__wrapper transitions transform, --m-duration-long). Scroll
       events stop the moment the reader does, so sampling on scroll alone left
       this bar frozen part-way through that slide — and it then snapped into
       place on the next scroll, which is the jump you see coming back to the
       top. Following for 600ms covers the header's whole animation.

       The writes above are all guarded on change, so the extra frames cost a
       couple of layout reads and nothing else. */
    function follow() {
      update();
      if (Date.now() < followUntil) {
        window.requestAnimationFrame(follow);
        return;
      }
      ticking = false;
    }

    function schedule() {
      followUntil = Date.now() + 600;
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(follow);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    update();
    // The theme sets its header height asynchronously, so re-measure once
    // everything has settled.
    window.addEventListener('load', update);
    setTimeout(update, 300);
  }

  /* 5. Sticky bar section menu (mobile) ----------------------------------- */

  /* Above 980px the anchor links are a row inside the bar and the toggle is
     display:none, so every listener below is inert there — no matchMedia
     needed, the CSS decides.

     Opening only flips a class. Scrolling to the section stays initAnchorNav's
     job: the panel holds the same <a href="#..."> elements it already binds. */
  function initStickyNavToggle(root) {
    var nav = root.querySelector('[data-nb2-nav]');
    if (!nav) return;

    var toggle = nav.querySelector('[data-nb2-nav-toggle]');
    if (!toggle || toggle.dataset.nb2Ready === 'true') return;
    toggle.dataset.nb2Ready = 'true';

    var closeButton = nav.querySelector('[data-nb2-nav-close]');
    var openedAtY = 0;

    function close() {
      if (!nav.classList.contains('is-open')) return;
      nav.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', function (event) {
      // Without this the document listener below sees the same click and
      // closes the drawer in the same tick it was opened.
      event.stopPropagation();
      var open = nav.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      openedAtY = window.scrollY;
      // Move into the drawer rather than leaving focus on the icon behind it.
      if (open && closeButton) closeButton.focus();
    });

    /* Scrolling the page puts the drawer away.

       Against a threshold rather than on any scroll event at all: iOS fires
       these as the address bar collapses and as momentum from the tap that
       opened the drawer settles, and either would shut it in the same breath
       it opened. 40px is past both and well short of a deliberate swipe. */
    window.addEventListener(
      'scroll',
      function () {
        if (!nav.classList.contains('is-open')) return;
        if (Math.abs(window.scrollY - openedAtY) < 40) return;
        close();
      },
      { passive: true }
    );

    /* Three ways out from inside the drawer: pick a section, hit Close, or
       tap the ground beside it. The ground is a child of the nav, so the
       document listener below never sees it as an outside click. */
    nav.addEventListener('click', function (event) {
      if (
        event.target.closest('a') ||
        event.target.closest('[data-nb2-nav-close]') ||
        event.target.closest('[data-nb2-nav-scrim]')
      ) {
        close();
      }
    });

    document.addEventListener('click', function (event) {
      if (!nav.contains(event.target)) close();
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !nav.classList.contains('is-open')) return;
      close();
      // Escape must not strand focus inside a panel that is now hidden.
      toggle.focus();
    });
  }

  /* 6. Anchor nav active state -------------------------------------------- */

  function initAnchorNav(root) {
    var nav = root.querySelector('[data-nb2-nav]');
    if (!nav || nav.dataset.nb2Ready === 'true') return;
    nav.dataset.nb2Ready = 'true';

    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    if (!links.length) return;

    var targets = links
      .map(function (link) {
        var id = link.getAttribute('href').slice(1);
        var el = id ? document.getElementById(id) : null;
        return el ? { link: link, el: el } : null;
      })
      .filter(Boolean);

    if (!targets.length) return;

    /* Scroll without leaving a fragment in the address bar.

       The markup stays a real <a href="#...">, so the links are still keyboard
       reachable, still open in a new tab on modified click, and still work with
       JavaScript off. This only takes over the plain left click.

       Why bother: every click used to write a hash, and the browser restores
       that hash on the next refresh — so reloading the page jumped the reader
       back to whichever section they last clicked. scroll-margin-top on the
       target still applies, so the heading clears the header and the bar. */
    links.forEach(function (link) {
      link.addEventListener('click', function (event) {
        if (event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        var match = targets.filter(function (t) { return t.link === link; })[0];
        if (!match) return;

        event.preventDefault();
        match.el.scrollIntoView({
          behavior: reducedMotion() ? 'auto' : 'smooth',
          block: 'start'
        });

        if (window.history && history.replaceState) {
          history.replaceState(null, '', location.pathname + location.search);
        }
      });
    });

    if (!('IntersectionObserver' in window)) return;

    /* Track which sections are in view, rather than reacting to the last one
       that entered. An add-only version leaves whatever was lit last still lit
       anywhere no target is intersecting — above the first one, in the hero. */
    var inView = [];

    function paint() {
      var current = null;

      // Topmost section in view wins, so scrolling back up hands the highlight
      // over in the order the reader actually sees them.
      targets.forEach(function (t) {
        if (inView.indexOf(t.el) === -1) return;
        if (!current || t.el.getBoundingClientRect().top < current.el.getBoundingClientRect().top) {
          current = t;
        }
      });

      links.forEach(function (l) {
        l.classList[current && l === current.link ? 'add' : 'remove']('is-active');
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var i = inView.indexOf(entry.target);
          if (entry.isIntersecting && i === -1) inView.push(entry.target);
          if (!entry.isIntersecting && i !== -1) inView.splice(i, 1);
        });
        paint();
      },
      // Activate a link when its section crosses the upper third of the screen.
      { rootMargin: '-25% 0px -65% 0px', threshold: 0 }
    );

    targets.forEach(function (t) { observer.observe(t.el); });
  }

  /* 6. Promo bar rotator -------------------------------------------------- */

  /* The top strip cross-fades between its messages. The fade itself is CSS
     (.nb2-promobar__msg / .is-current); this only moves the class.

     Not the theme's Swiper announcement bar: that is a real slider with drag,
     nav and a wrapper element, and this is two absolutely-stacked nodes
     swapping opacity. Pulling Swiper in for it would also be the only
     dependency in any nb2-* asset. */
  function initPromoBar(root) {
    var stacks = root.querySelectorAll('[data-nb2-promobar]');

    Array.prototype.forEach.call(stacks, function (stack) {
      if (stack.dataset.nb2Ready === 'true') return;
      stack.dataset.nb2Ready = 'true';

      var messages = stack.querySelectorAll('.nb2-promobar__msg');
      // One message is a static bar. Leave it alone rather than running a timer
      // that swaps a node for itself.
      if (messages.length < 2) return;

      // The setting is in seconds and is a range, so it cannot arrive as NaN
      // from the editor — but it can from a hand-edited template.
      var speed = parseFloat(stack.dataset.nb2Speed) * 1000;
      if (!speed || speed < 1000) speed = 5000;

      var index = 0;
      var timer = null;
      var paused = false;

      function show(next) {
        messages[index].classList.remove('is-current');
        messages[next].classList.add('is-current');
        index = next;
      }

      /* setTimeout chained rather than setInterval: removing the section in the
         theme editor detaches these nodes, and an interval would go on firing
         against them for the life of the page. The containment check is what
         ends the chain. */
      function queue() {
        timer = window.setTimeout(function () {
          if (!document.contains(stack)) return;
          if (!paused) show((index + 1) % messages.length);
          queue();
        }, speed);
      }

      /* Hovering or tabbing in holds the current message. Someone reading the
         offer, or on their way to clicking it, should not have it swapped out
         from under them. */
      function pause() { paused = true; }
      function resume() { paused = false; }

      stack.addEventListener('mouseenter', pause);
      stack.addEventListener('mouseleave', resume);
      stack.addEventListener('focusin', pause);
      stack.addEventListener('focusout', resume);

      // A background tab throttles timers unevenly; restarting the wait on
      // return avoids coming back to a message that flips immediately.
      document.addEventListener('visibilitychange', function () {
        if (!document.contains(stack)) return;
        window.clearTimeout(timer);
        if (!document.hidden) queue();
      });

      queue();
    });
  }

  /* Boot ------------------------------------------------------------------ */

  function init(root) {
    var scope = root || document;
    initVariantToggle(scope);
    initFaq(scope);
    initReviewCarousel(scope);
    initStickyBar(scope);
    initStickyNavToggle(scope);
    initAnchorNav(scope);
    initPromoBar(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // Removing the sticky bar in the theme editor has to give the theme its own
  // mobile bar back, or the page is left with neither.
  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.querySelector('[data-nb2-sticky-bar]')) {
      document.body.classList.remove('nb2-hide-theme-mobile-bar');
    }
  });
})();
